import React from 'react';
import { twMerge } from 'tailwind-merge';
import { Status } from '../types';
import { X } from 'lucide-react';
// --- Badge ---
export function Badge({
  status,
  className
}: {
  status: Status;
  className?: string;
}) {
  const baseStyles = 'px-2.5 py-0.5 rounded-full text-xs font-medium border';
  const statusStyles = {
    Pending: 'bg-amber-50 text-amber-700 border-amber-200',
    Approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Rejected: 'bg-red-50 text-red-700 border-red-200'
  };
  return <span className={twMerge(baseStyles, statusStyles[status], className)} data-id="element-221">
      {status}
    </span>;
}
// --- Button ---
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'brand' | 'neutral' | 'destructive' | 'ghost';
}
export function Button({
  variant = 'neutral',
  className,
  ...props
}: ButtonProps) {
  const baseStyles = 'inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  const variants = {
    brand: 'bg-[#0176d3] text-white hover:bg-[#014486] focus:ring-[#0176d3] border border-transparent',
    neutral: 'bg-white text-[#0176d3] border border-gray-300 hover:bg-gray-50 focus:ring-[#0176d3]',
    destructive: 'bg-white text-red-600 border border-red-300 hover:bg-red-50 focus:ring-red-500',
    ghost: 'bg-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:ring-gray-500 border border-transparent'
  };
  return <button className={twMerge(baseStyles, variants[variant], className)} {...props} data-id="element-222" />;
}
// --- Modal ---
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: string;
}
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  maxWidth = 'max-w-lg'
}: ModalProps) {
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-gray-900/50 backdrop-blur-sm p-4" data-id="element-223">
      <div className={twMerge('relative w-full bg-white rounded-lg shadow-xl flex flex-col max-h-[90vh]', maxWidth)} data-id="element-224">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-225">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-226">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0176d3] rounded" data-id="element-227">
            <X className="w-5 h-5" data-id="element-228" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto" data-id="element-229">{children}</div>

        {/* Footer */}
        {footer && <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg space-x-3" data-id="element-230">
            {footer}
          </div>}
      </div>
    </div>;
}